import { cn } from '../lib/cn';

/** A count, or the collection being counted. The chip only ever renders a
 *  number, so a caller holding the items themselves does not have to remember
 *  `.length` — and the queue hook is free to return either without this
 *  component and that one disagreeing at the type level. */
type Countable = number | { readonly length: number };

/**
 * A count, or `null` for "we could not find out".
 *
 * ⚠⚠ THE WHOLE POINT OF THIS TYPE. The chip's job is to say whether the readings
 * have left the device, and it used to say "synced" whenever the count was zero
 * — including when the count was zero because IndexedDB threw and the queue had
 * answered 0 rather than admitting it could not look. A device in private
 * browsing, or with a corrupt or full store, told the operator everything was
 * sent. That is the same class of lie as a refused reading disappearing, on the
 * one line of text they actually read.
 */
type CountableOrUnknown = Countable | null;

interface SyncChipProps {
  /** Items waiting to be sent that belong to the signed-in account, or `null`
   *  when the device could not read its own queue. Never fold null into 0.
   *  ⚠ EVERY queue on the device, not just the readings: the operator shell passes
   *  readings + dock inspections + dock photos (syncChipCounts, UI/UX audit, 31 Oct
   *  2026, UX-09) - a chip fed one queue said "synced" over inspections waiting to go. */
  queued: CountableOrUnknown;
  /**
   * Items in the queue that were keyed by a DIFFERENT account and are therefore
   * never sent by this one. They are counted here rather than hidden because a
   * held item is a reading somebody took that the database does not have — the
   * only way it gets there is if the person who keyed it signs in.
   */
  held: Countable;
  online: boolean;
  className?: string;
}

function count(value: Countable): number {
  const n = typeof value === 'number' ? value : value.length;
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

/**
 * The queue's state in one line.
 *
 * Props only, no store access, on purpose: the offline module owns the queue and
 * this renders whatever it is given, so the chip can be tested with four numbers
 * and no IndexedDB, and can be dropped into either shell.
 */
export function SyncChip({ queued, held, online, className }: SyncChipProps) {
  // Unknown is its own state and is checked FIRST, because every other branch
  // below reads a number and would turn "we cannot look" into "there is nothing
  // there". `held` is equally unknown in this state, so it is not appended
  // either — a "0 held" beside an unreadable queue is a second false claim.
  const unknown = queued === null;
  const waiting = unknown ? 0 : count(queued);
  const blocked = count(held);

  let label: string;
  if (unknown) label = online ? 'queue unreadable' : 'queue unreadable, offline';
  else if (waiting > 0 && !online) label = `${waiting} queued, offline`;
  else if (waiting > 0) label = `${waiting} queued`;
  else if (!online) label = 'offline';
  else label = 'synced';

  if (!unknown && blocked > 0) label += `, ${blocked} held`;

  // Amber, never red. Red in this app means out of spec, and a full queue on a
  // dropped network is the system working as designed — the readings are safe and
  // will land. Painting it red would teach the line to ignore the one colour that
  // has to keep meaning something.
  const attention = unknown || waiting > 0 || blocked > 0 || !online;

  return (
    <span
      // Announced when it changes, so an operator who is looking at the keypad
      // rather than the header still learns that the network came back.
      role="status"
      aria-live="polite"
      className={cn(
        // 14px, not the 12px micro step: index.css keeps 12px for units and
        // column labels, and this chip is the one line of text on the operator
        // screen that says whether their readings have actually left the device.
        'inline-flex items-center gap-s2 rounded border px-s2 py-s1 text-body font-medium',
        attention
          ? 'border-action-line bg-action-bg text-action-fg'
          : 'border-hairline bg-elevated text-ink-muted',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-2 w-2 rounded-full',
          attention ? 'bg-action-fg' : 'bg-ink-faint',
        )}
      />
      {label}
    </span>
  );
}

// Named export is the one to use; the default is an alias so an
// `import SyncChip from` resolves rather than breaking a file nobody is editing.
export default SyncChip;
